import { format } from 'date-fns';

/**
 * Get the date range for a given week number in a year
 * Always returns Sunday-Saturday range while preserving fiscal week numbering
 * 
 * @param {number} year - The year
 * @param {number} weekNumber - The week number (1-52)
 * @returns {{ startDate: Date, endDate: Date }} The date range for the week
 */
export const getWeekRange = (year, weekNumber) => {
    console.log('getWeekRange input:', { year, weekNumber });
    
    // Get all weeks in the year
    const weeksInYear = getWeeksInYear(year);
    
    // Find the week with the matching week number
    const matchingWeek = weeksInYear.find(week => week.weekNumber === weekNumber);
    
    if (matchingWeek) {
        // Convert string dates to Date objects
        const startDate = new Date(Date.UTC(year, 0, 1, 0, 0, 0, 0));
        const endDate = new Date(Date.UTC(year, 0, 1, 0, 0, 0, 0));
        
        // Parse the date strings (format: "MMM d, yyyy")
        const startParts = matchingWeek.startDate.split(' ');
        const endParts = matchingWeek.endDate.split(' ');
        
        // Extract month, day, year
        const startMonth = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].indexOf(startParts[0]);
        const startDay = parseInt(startParts[1].replace(',', ''));
        const startYear = parseInt(startParts[2]);
        
        const endMonth = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].indexOf(endParts[0]);
        const endDay = parseInt(endParts[1].replace(',', ''));
        const endYear = parseInt(endParts[2]);
        
        // Set the date components
        startDate.setUTCFullYear(startYear, startMonth, startDay);
        endDate.setUTCFullYear(endYear, endMonth, endDay);
        endDate.setUTCHours(23, 59, 59, 999);
        
        console.log('getWeekRange output (from weeksInYear):', {
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
            startDay: startDate.getUTCDay(), // 0 = Sunday
            endDay: endDate.getUTCDay()  // 6 = Saturday
        });
        
        return { startDate, endDate };
    }
    
    // If no matching week is found, fall back to the original calculation
    console.log('No matching week found, falling back to original calculation');
    
    // Special handling for week 1
    if (weekNumber === 1) {
        // Find January 1st of the year
        const jan1 = new Date(Date.UTC(year, 0, 1, 0, 0, 0, 0));
        
        // Find the Sunday of the week containing January 1st
        // If January 1st is a Sunday, this will be January 1st
        // Otherwise, it will be the previous Sunday (which might be in the previous year)
        const startDate = new Date(Date.UTC(
            jan1.getUTCFullYear(),
            jan1.getUTCMonth(),
            jan1.getUTCDate() - jan1.getUTCDay(),
            0, 0, 0, 0
        ));
        
        // If the Sunday is in the previous year, we need to check if this is really week 1
        // Week 1 is the week containing January 1st
        if (startDate.getUTCFullYear() < year) {
            // Check if this week contains January 1st
            const endOfWeek = new Date(Date.UTC(
                startDate.getUTCFullYear(),
                startDate.getUTCMonth(),
                startDate.getUTCDate() + 6,
                23, 59, 59, 999
            ));
            
            // If this week doesn't contain January 1st, then it's not week 1
            // In this case, week 1 starts on the first Sunday of the year
            if (endOfWeek.getUTCFullYear() < year) {
                // Find the first Sunday of the year
                const firstSunday = new Date(Date.UTC(
                    year,
                    0,
                    1 + (7 - jan1.getUTCDay()) % 7,
                    0, 0, 0, 0
                ));
                
                const endDate = new Date(Date.UTC(
                    firstSunday.getUTCFullYear(),
                    firstSunday.getUTCMonth(),
                    firstSunday.getUTCDate() + 6,
                    23, 59, 59, 999
                ));
                
                console.log('getWeekRange output for week 1 (first Sunday of year):', {
                    startDate: firstSunday.toISOString(),
                    endDate: endDate.toISOString(),
                    startDay: firstSunday.getUTCDay(), // 0 = Sunday
                    endDay: endDate.getUTCDay()  // 6 = Saturday
                });
                
                return { startDate: firstSunday, endDate };
            }
        }
        
        // Week 1 is the week containing January 1st
        const endDate = new Date(Date.UTC(
            startDate.getUTCFullYear(),
            startDate.getUTCMonth(),
            startDate.getUTCDate() + 6,
            23, 59, 59, 999
        ));
        
        console.log('getWeekRange output for week 1:', {
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
            startDay: startDate.getUTCDay(), // 0 = Sunday
            endDay: endDate.getUTCDay()  // 6 = Saturday
        });
        
        return { startDate, endDate };
    }
    
    // Special handling for last week of previous year (week 52 or 53)
    if (weekNumber >= 52 && year > 1) {
        // Check if this might be the last week of the previous year
        // This happens when the week contains days from both December and January
        
        // Get December 31st of the previous year
        const dec31 = new Date(Date.UTC(year - 1, 11, 31, 0, 0, 0, 0));
        
        // Find the Sunday of the week containing December 31st
        const lastWeekSunday = new Date(Date.UTC(
            dec31.getUTCFullYear(),
            dec31.getUTCMonth(),
            dec31.getUTCDate() - dec31.getUTCDay(),
            0, 0, 0, 0
        ));
        
        // Check if this week extends into the current year
        const lastWeekSaturday = new Date(Date.UTC(
            lastWeekSunday.getUTCFullYear(),
            lastWeekSunday.getUTCMonth(),
            lastWeekSunday.getUTCDate() + 6,
            23, 59, 59, 999
        ));
        
        // If this week contains days in the current year and the week number matches
        if (lastWeekSaturday.getUTCFullYear() === year) {
            // Calculate the week number for this week
            const { weekNumber: lastWeekNumber } = getWeekInfo(lastWeekSunday);
            
            // If this is the week we're looking for
            if (lastWeekNumber === weekNumber) {
                console.log('getWeekRange output for last week of previous year:', {
                    startDate: lastWeekSunday.toISOString(),
                    endDate: lastWeekSaturday.toISOString(),
                    startDay: lastWeekSunday.getUTCDay(), // 0 = Sunday
                    endDay: lastWeekSaturday.getUTCDay()  // 6 = Saturday
                });
                
                return { startDate: lastWeekSunday, endDate: lastWeekSaturday };
            }
        }
    }
    
    // For other weeks, calculate based on the first Sunday of the year
    // Find January 1st
    const jan1 = new Date(Date.UTC(year, 0, 1, 0, 0, 0, 0));
    
    // Find the first Sunday of the year
    const firstSunday = new Date(Date.UTC(
        year,
        0,
        1 + (7 - jan1.getUTCDay()) % 7,
        0, 0, 0, 0
    ));
    
    // Calculate the start date for the requested week
    const startDate = new Date(Date.UTC(
        firstSunday.getUTCFullYear(),
        firstSunday.getUTCMonth(),
        firstSunday.getUTCDate() + (weekNumber - 1) * 7,
        0, 0, 0, 0
    ));
    
    // Calculate the end date (Saturday of the same week)
    const endDate = new Date(Date.UTC(
        startDate.getUTCFullYear(),
        startDate.getUTCMonth(),
        startDate.getUTCDate() + 6,
        23, 59, 59, 999
    ));
    
    console.log('getWeekRange output:', {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        startDay: startDate.getUTCDay(), // 0 = Sunday
        endDay: endDate.getUTCDay()  // 6 = Saturday
    });
    
    return { startDate, endDate };
};

/**
 * Parse a UTC date string to a Date object, preserving UTC time
 * @param {string} dateString - UTC date string
 * @returns {Date} Date object
 */
export const parseUTCDate = (dateString) => {
    const date = new Date(dateString);
    return new Date(Date.UTC(
        date.getUTCFullYear(),
        date.getUTCMonth(),
        date.getUTCDate(),
        0, 0, 0, 0
    ));
};

/**
 * Format a date in UTC
 * @param {Date} date - Date to format
 * @param {string} formatStr - Format string
 * @returns {string} Formatted date string
 */
export const formatUTCDate = (date, formatStr) => {
    // Create a new date at midnight UTC
    const utcDate = new Date(Date.UTC(
        date.getUTCFullYear(),
        date.getUTCMonth(),
        date.getUTCDate()
    ));
    
    // Create a local date with the same UTC components
    const localDate = new Date(
        utcDate.getUTCFullYear(),
        utcDate.getUTCMonth(),
        utcDate.getUTCDate()
    );
    
    return format(localDate, formatStr);
};

/**
 * Check if a week spans across months
 * @param {string} startDateStr - Start date string from database
 * @param {string} endDateStr - End date string from database
 * @returns {{ isMonthBoundary: boolean, endOfMonthDays: string[], newMonthDays: string[] }}
 */
export const checkMonthBoundary = (startDateStr, endDateStr) => {
    // Extract month from date strings (format: "YYYY-MM-DDT00:00:00.000Z")
    const startMonth = parseInt(startDateStr.split('-')[1]);
    const endMonth = parseInt(endDateStr.split('-')[1]);

    // Check if week spans different months
    const isMonthBoundary = startMonth !== endMonth;
    
    if (!isMonthBoundary) {
        return {
            isMonthBoundary: false,
            endOfMonthDays: [],
            newMonthDays: []
        };
    }

    // Get all days in the week
    const weekDays = [];
    const start = new Date(startDateStr);
    const end = new Date(endDateStr);
    let currentDate = new Date(start);

    while (currentDate <= end) {
        weekDays.push(currentDate.toISOString());
        currentDate.setUTCDate(currentDate.getUTCDate() + 1);
    }

    // Split days into end of month and new month
    const endOfMonthDays = weekDays.filter(dateStr => 
        parseInt(dateStr.split('-')[1]) === startMonth
    );
    
    const newMonthDays = weekDays.filter(dateStr => 
        parseInt(dateStr.split('-')[1]) === endMonth
    );

    return {
        isMonthBoundary: true,
        endOfMonthDays,
        newMonthDays
    };
};

/**
 * Get week number and year for a date
 * Week 1 is the week containing January 1st
 * @param {Date} date - Date to get week number for
 * @returns {{ weekNumber: number, year: number }}
 */
export const getWeekInfo = (date) => {
    console.log('getWeekInfo input:', date.toISOString());
    
    // Create a UTC date to avoid timezone issues
    const target = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 0, 0, 0, 0));
    
    console.log('getWeekInfo - target UTC date:', {
        date: target.toISOString(),
        month: target.getUTCMonth(), // 11 = December
        day: target.getUTCDate(),
        dayOfWeek: target.getUTCDay() // 0 = Sunday
    });
    
    // Get the Sunday of the week containing the target date
    const targetSunday = new Date(Date.UTC(
        target.getUTCFullYear(),
        target.getUTCMonth(),
        target.getUTCDate() - target.getUTCDay(),
        0, 0, 0, 0
    ));
    
    // Determine the year this week belongs to
    // If it's late December, check if this week contains days in January of next year
    let weekYear = targetSunday.getUTCFullYear();
    
    // If it's December and the week extends into January of next year
    if (targetSunday.getUTCMonth() === 11) {
        const nextSaturday = new Date(Date.UTC(
            targetSunday.getUTCFullYear(),
            targetSunday.getUTCMonth(),
            targetSunday.getUTCDate() + 6,
            0, 0, 0, 0
        ));
        
        // If the Saturday of this week is in January of next year, this is week 1 of next year
        if (nextSaturday.getUTCMonth() === 0) {
            return { weekNumber: 1, year: weekYear + 1 };
        }
    }
    
    // If it's January and the week starts in December of previous year
    if (targetSunday.getUTCMonth() === 0 && targetSunday.getUTCDate() <= 7) {
        const prevSunday = new Date(Date.UTC(
            targetSunday.getUTCFullYear(),
            targetSunday.getUTCMonth(),
            targetSunday.getUTCDate() - 7,
            0, 0, 0, 0
        ));
        
        // If the previous Sunday is in December, check if that week contains January 1
        if (prevSunday.getUTCMonth() === 11) {
            const nextSaturday = new Date(Date.UTC(
                prevSunday.getUTCFullYear(),
                prevSunday.getUTCMonth(),
                prevSunday.getUTCDate() + 6,
                0, 0, 0, 0
            ));
            
            // If the Saturday of the previous week is in January, that was week 1
            if (nextSaturday.getUTCMonth() === 0) {
                // This means the current week is week 2
                return { weekNumber: 2, year: weekYear };
            }
        }
    }
    
    // Get the first day of the year
    const firstDay = new Date(Date.UTC(weekYear, 0, 1, 0, 0, 0, 0));
    
    // Find the Sunday of the week containing January 1st
    const firstWeekSunday = new Date(Date.UTC(
        firstDay.getUTCFullYear(),
        firstDay.getUTCMonth(),
        firstDay.getUTCDate() - firstDay.getUTCDay(),
        0, 0, 0, 0
    ));
    
    // Calculate weeks since the first week Sunday (always use the week containing Jan 1)
    const millisecPerDay = 24 * 60 * 60 * 1000;
    const daysSinceFirstSunday = Math.round((targetSunday - firstWeekSunday) / millisecPerDay);
    let weekNumber = Math.floor(daysSinceFirstSunday / 7) + 1;
    
    // If the target date is in the last week of December
    if (target.getUTCMonth() === 11 && target.getUTCDate() >= 24) {
        // Check if this week contains January 1 of next year
        const nextYearJan1 = new Date(Date.UTC(target.getUTCFullYear() + 1, 0, 1, 0, 0, 0, 0));
        const daysUntilNextYear = Math.round((nextYearJan1 - target) / millisecPerDay);
        
        // If January 1 is within the current week
        if (daysUntilNextYear <= 6 - target.getUTCDay()) {
            return { weekNumber: 1, year: weekYear + 1 };
        }
    }
    
    // Special case for the last few days of December that might be part of week 1 of next year
    if (target.getUTCMonth() === 11 && target.getUTCDate() >= 29) {
        // Get the first week of next year
        const nextYear = target.getUTCFullYear() + 1;
        const nextYearFirstDay = new Date(Date.UTC(nextYear, 0, 1, 0, 0, 0, 0));
        const nextYearFirstSunday = new Date(Date.UTC(
            nextYearFirstDay.getUTCFullYear(),
            nextYearFirstDay.getUTCMonth(),
            nextYearFirstDay.getUTCDate() - nextYearFirstDay.getUTCDay(),
            0, 0, 0, 0
        ));
        
        // If the first Sunday of next year is in the current year, this is week 1 of next year
        if (nextYearFirstSunday.getUTCFullYear() === target.getUTCFullYear()) {
            if (target >= nextYearFirstSunday) {
                return { weekNumber: 1, year: nextYear };
            }
        }
    }
    
    // Handle the case where the date is in the first few days of January but belongs to week 52/53 of previous year
    if (target.getUTCMonth() === 0 && target.getUTCDate() <= 7 && targetSunday.getUTCMonth() === 11) {
        // This is the last week of the previous year
        const prevYear = target.getUTCFullYear() - 1;
        
        // Calculate the week number for the last week of the previous year
        const lastDayPrevYear = new Date(Date.UTC(prevYear, 11, 31, 0, 0, 0, 0));
        const lastWeekSunday = new Date(Date.UTC(
            lastDayPrevYear.getUTCFullYear(),
            lastDayPrevYear.getUTCMonth(),
            lastDayPrevYear.getUTCDate() - lastDayPrevYear.getUTCDay(),
            0, 0, 0, 0
        ));
        
        // Get the first Sunday of the previous year
        const firstDayPrevYear = new Date(Date.UTC(prevYear, 0, 1, 0, 0, 0, 0));
        const firstSundayPrevYear = new Date(Date.UTC(
            firstDayPrevYear.getUTCFullYear(),
            firstDayPrevYear.getUTCMonth(),
            firstDayPrevYear.getUTCDate() + (7 - firstDayPrevYear.getUTCDay()) % 7,
            0, 0, 0, 0
        ));
        
        // Calculate the week number
        const weeksInPrevYear = Math.round((lastWeekSunday - firstSundayPrevYear) / (7 * millisecPerDay)) + 1;
        return { weekNumber: weeksInPrevYear, year: prevYear };
    }
    
    const result = { weekNumber, year: weekYear };
    console.log('getWeekInfo output:', result);
    return result;
};

/**
 * Get week dates based on a reference date
 * @param {Date} referenceDate - Date to get week range for
 * @returns {{ weekDates: Array, weekNum: string, dateRange: string, startDate: Date, endDate: Date, year: number }}
 */
export const getWeekDates = (referenceDate) => {
    const { weekNumber, year } = getWeekInfo(referenceDate);
    
    // Get the Sunday of the week containing the reference date
    const startDate = new Date(Date.UTC(
        referenceDate.getUTCFullYear(),
        referenceDate.getUTCMonth(),
        referenceDate.getUTCDate() - referenceDate.getUTCDay(), // Subtract days to get to Sunday
        0, 0, 0, 0
    ));
    
    // Get the Saturday (end of week)
    const endDate = new Date(Date.UTC(
        startDate.getUTCFullYear(),
        startDate.getUTCMonth(),
        startDate.getUTCDate() + 6, // Add 6 days to get to Saturday
        23, 59, 59, 999
    ));
    
    // Generate week dates starting from Sunday
    const weekDates = [];
    let currentDate = new Date(Date.UTC(
        startDate.getUTCFullYear(),
        startDate.getUTCMonth(),
        startDate.getUTCDate(),
        0, 0, 0, 0
    ));
    for (let i = 0; i < 7; i++) {
        const date = new Date(Date.UTC(
            currentDate.getUTCFullYear(),
            currentDate.getUTCMonth(),
            currentDate.getUTCDate(),
            0, 0, 0, 0
        ));
        weekDates.push({
            full: date,
            display: formatUTCDate(date, 'MMM d'),
            dayName: formatUTCDate(date, 'EEE')
        });
        currentDate = new Date(Date.UTC(
            currentDate.getUTCFullYear(),
            currentDate.getUTCMonth(),
            currentDate.getUTCDate() + 1,
            0, 0, 0, 0
        ));
    }
    
    // Format date range using the weekDates array to ensure consistency
    const dateRange = `${weekDates[0].display} - ${weekDates[6].display}`;
    
    console.log('getWeekDates - final output:', {
        weekNum: weekNumber.toString(),
        dateRange,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        weekDates: weekDates.map(d => ({
            date: d.full.toISOString(),
            display: d.display,
            dayName: d.dayName
        }))
    });
    
    return {
        weekDates,
        weekNum: weekNumber.toString(),
        dateRange,
        startDate,
        endDate,
        year
    };
};

/**
 * Get all weeks in a year with their date ranges
 * @param {number} year - Year to get weeks for
 * @returns {Array<{ weekNumber: number, startDate: string, endDate: string, value: number }>}
 */
export const getWeeksInYear = (year) => {
    const weeks = [];
    
    // Find January 1st of the year
    const jan1 = new Date(Date.UTC(year, 0, 1, 0, 0, 0, 0));
    
    // Find the Sunday of the week containing January 1st (this is always Week 1)
    const week1Sunday = new Date(Date.UTC(
        jan1.getUTCFullYear(),
        jan1.getUTCMonth(),
        jan1.getUTCDate() - jan1.getUTCDay(),
        0, 0, 0, 0
    ));
    
    // Generate weeks sequentially starting from Week 1
    let currentSunday = new Date(week1Sunday);
    let weekNumber = 1;
    
    // Continue until we've covered the entire year
    while (weekNumber <= 53) { // Maximum possible weeks in a year
        const startDate = new Date(currentSunday);
        const endDate = new Date(Date.UTC(
            startDate.getUTCFullYear(),
            startDate.getUTCMonth(),
            startDate.getUTCDate() + 6,
            23, 59, 59, 999
        ));
        
        // Check if this week belongs to the requested year
        // A week belongs to a year if it contains January 1st of that year OR
        // if it starts in that year
        const weekContainsJan1 = (startDate <= jan1 && endDate >= jan1);
        const weekStartsInYear = startDate.getUTCFullYear() === year;
        
        // Additional check: make sure this week doesn't belong to the next year
        // Use getWeekInfo to determine which year this week actually belongs to
        const weekInfo = getWeekInfo(startDate);
        const weekBelongsToRequestedYear = weekInfo.year === year;
        
        if ((weekContainsJan1 || weekStartsInYear) && weekBelongsToRequestedYear) {
            weeks.push({
                weekNumber: weekNumber,
                startDate: formatUTCDate(startDate, 'MMM d, yyyy'),
                endDate: formatUTCDate(endDate, 'MMM d, yyyy'),
                value: weekNumber
            });
        }
        
        // Move to next week
        currentSunday = new Date(Date.UTC(
            currentSunday.getUTCFullYear(),
            currentSunday.getUTCMonth(),
            currentSunday.getUTCDate() + 7,
            0, 0, 0, 0
        ));
        weekNumber++;
        
        // Stop if we've gone well past the year
        if (currentSunday.getUTCFullYear() > year + 1) {
            break;
        }
    }
    
    return weeks;
};
